import { Injectable } from "@angular/core";
import { NzModalService } from "ng-zorro-antd/modal";
import { Observable } from "rxjs";
import { take } from "rxjs/operators";
import type { ConfirmWithReasonModalData } from "@shared/common-components/confirm-with-reason-modal/confirm-with-reason-modal.component";
import type { ApproveBaseLookFirstModalData } from "@shared/common-components/approve-base-look-first-modal/approve-base-look-first-modal.component";
import type { ConfirmWithPrimaryModalData } from "@shared/common-components/confirm-with-primary-modal/confirm-with-primary-modal.component";

@Injectable({
  providedIn: "root",
})
export class ConfirmService {
  /** Above image-preview-overlay (1001) and default ng-zorro modals (1000). */
  private readonly modalZIndex = 10050;
  private confirmDialogDepth = 0;

  constructor(private modal: NzModalService) {}

  private pushConfirmDialog(): void {
    this.confirmDialogDepth += 1;
    document.body.classList.add("confirm-dialog-open");
  }

  private popConfirmDialog(): void {
    this.confirmDialogDepth = Math.max(0, this.confirmDialogDepth - 1);
    if (this.confirmDialogDepth === 0) {
      document.body.classList.remove("confirm-dialog-open");
    }
  }

  private trackConfirmDialogClose(modalRef: {
    afterClose: Observable<unknown>;
  }): void {
    this.pushConfirmDialog();
    modalRef.afterClose.pipe(take(1)).subscribe(() => {
      this.popConfirmDialog();
    });
  }

  /**
   * Confirmation with a third, primary way out — e.g. "Save and leave"
   * alongside "Leave" and "Stay". `confirm()` only models two outcomes.
   */
  confirmWithPrimary(opts: {
    title: string;
    content?: string;
    /** The recommended action. Rendered as the primary button. */
    primaryText: string;
    /** The destructive one. */
    okText: string;
    cancelText?: string;
  }): Observable<"primary" | "ok" | "cancel"> {
    return new Observable((observer) => {
      import(
        "@shared/common-components/confirm-with-primary-modal/confirm-with-primary-modal.component"
      ).then(({ ConfirmWithPrimaryModalComponent }) => {
        const modalRef = this.modal.create<
          any,
          ConfirmWithPrimaryModalData,
          "primary" | "ok" | "cancel" | null
        >({
          nzTitle: "",
          nzContent: ConfirmWithPrimaryModalComponent,
          nzData: {
            title: opts.title,
            message: opts.content || "",
            primaryText: opts.primaryText,
            okText: opts.okText,
            cancelText: opts.cancelText || "Cancel",
          },
          nzCentered: true,
          nzZIndex: this.modalZIndex,
          nzWrapClassName:
            "common-modal-design confirm-with-primary-modal-wrap",
          nzMaskClosable: false,
          nzClosable: true,
          nzFooter: null,
        });

        this.trackConfirmDialogClose(modalRef);
        modalRef.afterClose.pipe(take(1)).subscribe((result) => {
          observer.next(result ?? "cancel");
          observer.complete();
        });
      });
    });
  }

  /**
   * Opens a reusable confirmation modal.
   * @param title - Title of the confirmation box
   * @param content - Optional message/content
   * @param okText - Text for OK button (default: "OK")
   * @param cancelText - Text for Cancel button (default: "Cancel")
   * @returns Observable<boolean> that emits true if OK clicked, false if cancelled
   */
  confirm(
    title: string,
    content?: string,
    okText: string = "OK",
    cancelText: string = "Cancel",
    autofocus: "ok" | "cancel" | null = "ok",
    options?: {
      maskClosable?: boolean;
      closable?: boolean;
    },
  ): Observable<boolean> {
    return new Observable((observer) => {
      const modalRef = this.modal.confirm({
        nzTitle: title,
        nzContent: content || "",
        nzOkText: okText,
        nzCancelText: cancelText,
        nzAutofocus: autofocus,
        nzCentered: true,
        nzZIndex: this.modalZIndex,
        nzClassName: "confirmation-modal",
        nzWrapClassName: "confirmation-modal-wrap",
        nzMaskClosable: options?.maskClosable ?? true,
        nzClosable: options?.closable ?? true,
        nzOnOk: () => {
          observer.next(true);
          observer.complete();
        },
        nzOnCancel: () => {
          observer.next(false);
          observer.complete();
        },
      });
      this.trackConfirmDialogClose(modalRef);
    });
  }

  /**
   * Opens a confirmation modal with an optional reason/justification text area.
   * @returns Observable that emits the reason string if confirmed, or null if cancelled
   */
  confirmWithReason(options: {
    title: string;
    message: string;
    okText?: string;
    cancelText?: string;
    reasonLabel?: string;
    reasonPlaceholder?: string;
    reasonRequired?: boolean;
  }): Observable<string | null> {
    return new Observable((observer) => {
      import(
        "@shared/common-components/confirm-with-reason-modal/confirm-with-reason-modal.component"
      ).then(({ ConfirmWithReasonModalComponent }) => {
        const modalRef = this.modal.create<any, ConfirmWithReasonModalData>({
          nzTitle: "",
          nzContent: ConfirmWithReasonModalComponent,
          nzData: {
            title: options.title,
            message: options.message,
            okText: options.okText ?? "Confirm",
            cancelText: options.cancelText ?? "Cancel",
            reasonLabel: options.reasonLabel ?? "Reason (optional)",
            reasonPlaceholder:
              options.reasonPlaceholder ?? "Enter your reason...",
            reasonRequired: options.reasonRequired ?? false,
          },
          nzFooter: null,
          nzClosable: true,
          nzCentered: true,
          nzZIndex: this.modalZIndex,
          nzWrapClassName: "common-modal-design confirm-with-reason-modal-wrap",
        });

        this.trackConfirmDialogClose(modalRef);

        modalRef.afterClose.subscribe((result: string | null) => {
          observer.next(result ?? null);
          observer.complete();
        });
      });
    });
  }

  /**
   * Opens an informational modal with a single OK button (no cancel).
   * Use for non-destructive notices where the user just needs to acknowledge.
   */
  info(title: string, content?: string, okText: string = "Got it"): void {
    const modalRef = this.modal.info({
      nzTitle: title,
      nzContent: content || "",
      nzOkText: okText,
      nzCentered: true,
      nzZIndex: this.modalZIndex,
      nzClassName: "confirmation-modal",
      nzWrapClassName: "confirmation-modal-wrap",
    });
    this.trackConfirmDialogClose(modalRef);
  }

  /**
   * Prompts the user to approve the base look before approving a dependent look.
   * Returns `'approve'` when the user chooses to approve the base look from the modal.
   */
  openApproveBaseLookFirstModal(
    data: ApproveBaseLookFirstModalData,
  ): Observable<"approve" | null> {
    return new Observable((observer) => {
      import(
        "@shared/common-components/approve-base-look-first-modal/approve-base-look-first-modal.component"
      ).then(({ ApproveBaseLookFirstModalComponent }) => {
        const modalRef = this.modal.create<any, ApproveBaseLookFirstModalData>({
          nzTitle: "",
          nzContent: ApproveBaseLookFirstModalComponent,
          nzData: data,
          nzFooter: null,
          nzClosable: true,
          nzCentered: true,
          nzZIndex: this.modalZIndex,
          nzWrapClassName:
            "common-modal-design approve-base-look-first-modal-wrap",
        });

        this.trackConfirmDialogClose(modalRef);

        modalRef.afterClose.subscribe((result: string | null) => {
          observer.next(result === "approve" ? "approve" : null);
          observer.complete();
        });
      });
    });
  }
}
